import { getApiClient } from "./apiClient";

// Backend types matching Django models
export interface EnrichedTrackEntity {
  id: string;
  track_id: string;
  kg_entity_id: string;
  kg_entity_type: "organization" | "facility" | "project";
  kg_namespace: string;
  first_discovered_at: string;
  metadata: Record<string, any>;

  // Live org status computed server-side from the current KG node:
  // true when status labels mark the org as dissolved/defunct.
  inactive?: boolean;

  // KG entity data (from Kuzu)
  kg_data: {
    id: string;
    name: string;
    description?: string;
    home_country?: string;
    country_iso2?: string;
    founding_year?: number;
    facility_type?: string | string[];
    project_types?: string[];
    labels?: string[];
    [key: string]: any;
  };

  // Latest reference data
  latest_reference: {
    id: string;
    relevance_score: number; // 0-1 from backend
    discovery_meta: Record<string, any>;
    discovered_at: string;
    spec_version_id: string | null;
    spec_version_number: number | null;
  } | null;

  // Latest decision data
  latest_decision: {
    id: string | null;
    decision_state:
      | "candidate"
      | "shortlisted"
      | "approved"
      | "rejected"
      | "final";
    decision_meta: Record<string, any>;
    decided_by: string | null;
    decided_at: string | null;
  };

  // Evaluation data (slim — no evidence items, no summary/reasoning)
  evaluationData?: {
    overall_score?: number;
    schema_version?: string;
    spec_version_changed?: boolean;
    eval_spec_version_number?: number;
    latest_spec_version_number?: number;
    criteria_results?: Record<
      string,
      {
        score: number;
        confidence: number;
        one_sentence_summary?: string;
        evidence_count?: number;
        label?: string;
        description?: string;
        weight?: number;
        is_stale?: boolean;
        stale_reason?: string;
      }
    >;
    executive_summary?: {
      overall_assessment: string;
      key_strengths: string[];
      key_concerns: string[];
      recommendation: string;
    };
    // Legacy fields from discovery
    decision?: "accept" | "reject" | "uncertain";
    confidence?: number;
    match_score?: number;
    match_signals?: string[];
    concerns?: string[];
  } | null;

  // Deep research status
  deepResearchStatus?: "not_started" | "in_progress" | "complete";

  // Per-candidate evaluation execution status (#888). Independent of
  // evaluationData (the last result): a re-queued candidate reads "queued"
  // while still showing its prior score.
  evaluationStatus?:
    | "none"
    | "queued"
    | "running"
    | "completed"
    | "failed"
    | "cancelled";
  evaluationTaskId?: string | null;
  evaluationRunId?: string | null;
  evaluationRunAt?: string | null;
  documentCount?: number;
}

// Evidence types for evaluation detail
export interface EvidenceSource {
  title: string;
  canonical_id: string;
  type: string;
  url: string | null;
  edge_id: string | null;
  entity_id: string | null;
  entity_name: string | null;
  source_id: string | null;
  publish_date: string | null;
}

export interface EvidenceItemData {
  id: string;
  claim_text: string;
  source: EvidenceSource;
  source_type?: string;
  criterion_relevance?: number;
  entity_specificity?: number;
  evidence_sentiment?: string;
  confidence?: number;
  score?: number;
  score_reasoning?: string;
  key_facts?: string[];
}

export interface EvidenceItemEntry extends EvidenceItemData {
  type: "item";
}

export interface ChainPathNode {
  entity_id: string;
  entity_name: string;
}

export interface ChainEdge {
  from_entity_id: string;
  to_entity_id: string;
  relation_type: string;
  edge_id: string;
}

export interface EvidenceGroupEntry {
  type: "group";
  group_id: string;
  label: string;
  group_type: string;
  synthesis_text: string;
  relevance_score?: number;
  confidence_score?: number;
  key_findings?: string[];
  chain_path?: ChainPathNode[];
  chain_edges?: ChainEdge[];
  evidence_items: EvidenceItemData[];
}

export type EvidenceEntry = EvidenceItemEntry | EvidenceGroupEntry;

// Ordered top-level citation reference — enough to number [cite:...] tokens
// without loading the evidence content itself.
export interface EvidenceRef {
  type: "item" | "group";
  id: string;
}

// Slim evaluation detail returned by the dedicated detail endpoint. Evidence
// items are NOT included; fetch them per criterion via getCriterionEvidence.
export interface EvaluationDetailResponse {
  evaluationData: {
    overall_score: number;
    schema_version: string;
    criteria_results: Record<
      string,
      {
        score: number;
        confidence: number;
        one_sentence_summary?: string;
        summary: string;
        reasoning: string;
        evidence_count?: number;
        evidence_refs?: EvidenceRef[];
        label?: string;
        description?: string;
        weight?: number;
      }
    >;
    executive_summary?: {
      overall_assessment: string;
      key_strengths: string[];
      key_concerns: string[];
      recommendation: string;
    };
  } | null;
  // Run/output actually resolved by the backend — pass run_id to
  // getCriterionEvidence so lazy fetches pin the same output even if a
  // newer evaluation completes in between.
  run_id?: string | null;
  output_id?: string | null;
}

export interface EvaluationRunSummary {
  run_id: string;
  started_at: string | null;
  finished_at: string | null;
  status: string;
  spec_version_id: string | null;
  spec_version_number: number | null;
  output_id: string;
}

export interface EnrichedEntitiesResponse {
  entities: EnrichedTrackEntity[];
}

export interface Activity {
  activity_id: string;
  timestamp: string;
  stage: string;
  action: string;
  details: string;
  activity_type: "spec_version" | "run" | "decision" | "manual_add" | "task";
  entity_id: string | null;
  user_username: string | null;
  user_email: string | null;
}

export interface RunStatus {
  run_id: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  run_type: "discovery" | "evaluation" | "research";
}

// Candidate-centric in-flight summary (#888): evaluations aggregate over tasks
// (the per-candidate unit); discovery stays run-level.
export interface ActiveWork {
  evaluation: { queued: number; running: number };
  discovery: { run_id: string; status: string }[];
}

export interface ActiveDiscoveryRun {
  run_id: string;
  project_id: string;
  project_name: string;
  track_id: string;
  track_name: string;
  status: string;
  scheduled_by: string | null;
  enqueued_at: string;
  started_at: string | null;
}

export interface ActiveEvalTask {
  task_id: string;
  track_entity_id: string | null;
  run_id: string;
  project_id: string;
  project_name: string;
  track_id: string;
  track_name: string;
  candidate_name: string;
  status: string;
  scheduled_by: string | null;
  enqueued_at: string;
  started_at: string | null;
}

export interface ActiveRunsResponse {
  discovery: ActiveDiscoveryRun[];
  evaluation: ActiveEvalTask[];
}

export interface ActivityResponse {
  activities: Activity[];
  run_status: RunStatus | null;
  active_work: ActiveWork;
  last_modified: string | null;
}

export interface ResearchTrackBasic {
  id: string;
  name: string;
  track_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ResearchProject {
  id: string;
  name: string;
  project_type: "tender" | "product_service" | "custom";
  status: "active" | "paused" | "completed" | "archived";
  created_by: string;
  created_by_username: string;
  context: Record<string, any>;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  track_count: number;
  tracks: ResearchTrackBasic[];
  permission_level:
    | "OWNER"
    | "EDITOR"
    | "REQ_EDITOR"
    | "VIEWER"
    | "NONE";
  shared_with_count: number;
  shared_with: {
    first_name: string;
    last_name: string;
    email: string;
    permission_level: "OWNER" | "EDITOR" | "REQ_EDITOR" | "VIEWER";
  }[];
}

export interface ProjectCollaborator {
  id: number;
  user: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  permission_level: "OWNER" | "EDITOR" | "REQ_EDITOR" | "VIEWER";
  created_at: string;
}

export interface ResearchTrack {
  id: string;
  project: string;
  project_name: string;
  name: string;
  track_type: "find_competitors" | "find_customers" | "find_partners";
  status: "active" | "paused" | "completed" | "archived";
  created_by: string;
  config: Record<string, any>;
  spec_count: number;
  run_count: number;
  entity_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectRequest {
  name: string;
  project_type?: "tender" | "product_service" | "custom";
  context?: Record<string, any>;
}

export interface UpdateProjectRequest {
  name?: string;
  project_type?: "tender" | "product_service" | "custom";
  status?: "active" | "paused" | "completed" | "archived";
  context?: Record<string, any>;
}

export interface ProjectListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ResearchProject[];
}

export interface TrackListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ResearchTrack[];
}

export interface SpecCriterion {
  id: string;
  spec_version: string;
  spec_version_number: number;
  criterion_id: string;
  label: string;
  criterion_type: string;
  weight: number;
  is_active: boolean;
  criterion_payload: {
    criterion_id: string;
    label: string;
    type: string;
    weight: number;
    is_active: boolean;
    description: string;
    rubric: Record<string, any>;
  };
  signature_hash: string;
  rubric_hash: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

export interface SpecVersion {
  id: string;
  track: string;
  track_name: string;
  spec_type: string;
  version_number: number;
  created_by: string;
  created_by_username: string;
  spec_payload: {
    schema_version: string;
    objective?: string;
    criteria?: Array<any>;
    [key: string]: any;
  };
  spec_meta: Record<string, any>;
  run_count: number;
  criteria: SpecCriterion[];
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

export type CriterionChangeType =
  | "added"
  | "removed"
  | "weight_changed"
  | "definition_changed"
  | "rubric_changed";

export interface CriterionChange {
  criterion_id: string;
  label: string;
  change_type: CriterionChangeType;
}

export interface SpecVersionDiff {
  objective_changed: boolean;
  criterion_changes: CriterionChange[];
}

export interface SpecVersionWithDiff extends SpecVersion {
  changes_since_prev: SpecVersionDiff;
}

export interface DomainAddResult {
  /** The raw pasted line this result belongs to. */
  input: string;
  status: "linked_existing" | "created_pending" | "already_in_track" | "invalid";
  domain?: string | null;
  track_entity_id?: string | null;
  kg_entity_id?: string | null;
  name?: string | null;
  profile_status?: "ready" | "queued" | "unavailable" | "error" | null;
}

/** Webcontent snapshot fields served for domain candidates (allowlisted server-side). */
export interface DomainProfileSnapshot {
  organization_name?: string | null;
  organization_description?: string | null;
  organization_type?: string | null;
  organization_ownership?: string | null;
  products?: string[] | null;
  services?: string[] | null;
  target_markets?: string[] | null;
  certifications?: string[] | null;
  organization_founding_year?: number | string | null;
  organization_number_of_employees?: number | string | null;
  organization_headquarters_city?: string | null;
  organization_headquarters_country?: string | null;
  organization_headquarters_country_iso2?: string | null;
}

/** A flagged relink conflict: the domain resolved to a graph entity the
 * track already carries as a separate candidate. */
export interface DomainCandidateConflict {
  kg_entity_id: string;
  name?: string | null;
  /** Crawl-observed redirect target, when the conflict came from a redirect. */
  redirected_to?: string | null;
  /** Whether removing this row would discard discoveries/evaluations. */
  has_history: boolean;
}

export interface DomainCandidateProfile {
  track_entity_id: string;
  domain: string | null;
  name?: string | null;
  profile_status: "ready" | "gathering" | "failed";
  error?: string | null;
  profile_source: string;
  pending_graph_ingestion: boolean;
  profile?: DomainProfileSnapshot | null;
  conflict?: DomainCandidateConflict | null;
}

class ResearchApiService {
  // Project CRUD operations
  async getProjects(params?: {
    search?: string;
    page?: number;
    status?: string;
  }): Promise<ProjectListResponse> {
    const client = await getApiClient();
    const response = await client.get("/research/projects/", { params });
    return response.data;
  }

  async getProject(projectId: string): Promise<ResearchProject> {
    const client = await getApiClient();
    const response = await client.get(`/research/projects/${projectId}/`);
    return response.data;
  }

  async createProject(data: CreateProjectRequest): Promise<ResearchProject> {
    const client = await getApiClient();
    const response = await client.post("/research/projects/", data);
    return response.data;
  }

  async updateProject(
    projectId: string,
    data: UpdateProjectRequest,
  ): Promise<ResearchProject> {
    const client = await getApiClient();
    const response = await client.patch(
      `/research/projects/${projectId}/`,
      data,
    );
    return response.data;
  }

  async deleteProject(projectId: string): Promise<void> {
    const client = await getApiClient();
    await client.post(`/research/projects/${projectId}/soft_delete/`);
  }

  async restoreProject(projectId: string): Promise<void> {
    const client = await getApiClient();
    await client.post(`/research/projects/${projectId}/restore/`);
  }

  async duplicateProject(projectId: string): Promise<ResearchProject> {
    const client = await getApiClient();
    const response = await client.post(
      `/research/projects/${projectId}/duplicate/`,
    );
    return response.data;
  }

  async shareProject(
    projectId: string,
    userPermissions: Array<{
      email: string;
      permission_level: "EDITOR" | "REQ_EDITOR" | "VIEWER";
      confirm_external?: "true";
    }>,
  ): Promise<{ status: string }> {
    const client = await getApiClient();
    const response = await client.patch(
      `/research/projects/${projectId}/share/`,
      { user_permissions: userPermissions },
    );
    return response.data;
  }

  async getCollaborators(projectId: string): Promise<ProjectCollaborator[]> {
    const client = await getApiClient();
    const response = await client.get(
      `/research/projects/${projectId}/collaborators/`,
    );
    return response.data;
  }

  // Track operations
  async getTracks(params?: {
    project?: string;
    page?: number;
  }): Promise<TrackListResponse> {
    const client = await getApiClient();
    const response = await client.get("/research/tracks/", { params });
    return response.data;
  }

  async createTrack(data: {
    project: string;
    name: string;
    track_type?: string;
  }): Promise<ResearchTrack> {
    const client = await getApiClient();
    const response = await client.post("/research/tracks/", data);
    return response.data;
  }

  async updateTrack(
    trackId: string,
    data: { name?: string; track_type?: string; status?: string },
  ): Promise<ResearchTrack> {
    const client = await getApiClient();
    const response = await client.patch(`/research/tracks/${trackId}/`, data);
    return response.data;
  }

  async deleteTrack(trackId: string): Promise<void> {
    const client = await getApiClient();
    await client.delete(`/research/tracks/${trackId}/`);
  }

  async getTrack(trackId: string): Promise<ResearchTrack> {
    const client = await getApiClient();
    const response = await client.get(`/research/tracks/${trackId}/`);
    return response.data;
  }

  async getTrackRequirements(trackId: string): Promise<SpecVersion | null> {
    const client = await getApiClient();
    try {
      const response = await client.get(
        `/research/tracks/${trackId}/latest_spec/`,
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No spec version exists yet
      }
      throw error;
    }
  }

  async getTrackSpecVersions(trackId: string): Promise<SpecVersionWithDiff[]> {
    const client = await getApiClient();
    const response = await client.get(
      `/research/tracks/${trackId}/spec_versions/`,
    );
    return response.data;
  }

  async updateTrackRequirements(
    trackId: string,
    requirements: { objective: string; criteria: any[] },
  ): Promise<SpecVersion> {
    const client = await getApiClient();
    const response = await client.post(
      `/research/tracks/${trackId}/update_requirements/`,
      requirements,
    );
    return response.data;
  }

  async triggerDiscovery(
    trackId: string,
    params?: {
      spec_version_id?: string;
      target_candidates?: number;
      max_total_scanned?: number;
      max_search_configs?: number;
      entities_per_query?: number;
    },
  ): Promise<any> {
    const client = await getApiClient();
    const response = await client.post(
      `/research/tracks/${trackId}/trigger_discovery/`,
      params || {},
    );
    return response.data;
  }

  async triggerEvaluation(
    trackId: string,
    params: {
      track_entity_ids: string[];
      spec_version_id?: string;
      // Re-evaluating reuses a candidate's existing sources for criteria that
      // have not changed and are still within the freshness window; set this to
      // look for new sources regardless of how recently they were gathered.
      force_refresh?: boolean;
    },
  ): Promise<any> {
    const client = await getApiClient();
    const response = await client.post(
      `/research/tracks/${trackId}/trigger_evaluation/`,
      params,
    );
    return response.data;
  }

  async cancelRun(runId: string): Promise<void> {
    const client = await getApiClient();
    await client.post(`/research/runs/${runId}/cancel/`);
  }

  // All in-flight discovery runs + evaluation candidate-tasks across every
  // project the user can access (incl. runs scheduled by other users).
  async getActiveRuns(): Promise<ActiveRunsResponse> {
    const client = await getApiClient();
    const response = await client.get("/research/runs/active/");
    return response.data;
  }

  // Cancel a single candidate's in-flight evaluation (siblings keep running).
  async cancelEvaluation(trackEntityId: string): Promise<void> {
    const client = await getApiClient();
    await client.post(
      `/research/track-entities/${trackEntityId}/cancel_evaluation/`,
    );
  }

  // Cancel every in-flight evaluation for a track ("Stop all").
  async cancelAllEvaluations(
    trackId: string,
  ): Promise<{ cancelled_runs: number }> {
    const client = await getApiClient();
    const response = await client.post(
      `/research/tracks/${trackId}/cancel_evaluations/`,
    );
    return response.data;
  }

  // Track Entity operations
  async getEnrichedEntities(
    trackId: string,
  ): Promise<EnrichedEntitiesResponse> {
    const client = await getApiClient();
    const response = await client.get("/research/track-entities/enriched/", {
      params: { track_id: trackId },
    });
    return response.data;
  }

  async addEntityToTrack(data: {
    track: string;
    kg_entity_id: string;
    kg_entity_type: "organization" | "facility" | "project";
    reason?: string;
    initial_decision_state?: "candidate" | "shortlisted";
  }): Promise<{ status: "created" | "already_exists"; id: string }> {
    const client = await getApiClient();
    const response = await client.post(
      "/research/track-entities/add_entity/",
      data,
    );
    return response.data;
  }

  async addCandidatesByDomains(data: {
    track: string;
    domains: string[];
    reason?: string;
    initial_decision_state?: "candidate" | "shortlisted";
  }): Promise<{ results: DomainAddResult[] }> {
    const client = await getApiClient();
    const response = await client.post(
      "/research/track-entities/add_by_domains/",
      data,
    );
    return response.data;
  }

  // Display profile for a not-yet-in-graph domain candidate (3-state: the
  // profile is fetched on demand, so this never 404s for an existing candidate).
  async getDomainCandidateProfile(
    trackId: string,
    kgEntityId: string,
  ): Promise<DomainCandidateProfile> {
    const client = await getApiClient();
    const response = await client.get(
      "/research/track-entities/domain-profile/",
      { params: { track_id: trackId, kg_entity_id: kgEntityId } },
    );
    return response.data;
  }

  // Soft-delete one candidate from its track (e.g. a relink duplicate).
  async removeTrackEntity(trackEntityId: string): Promise<void> {
    const client = await getApiClient();
    await client.post(`/research/track-entities/${trackEntityId}/remove/`);
  }

  async bulkAddEntitiesToTrack(data: {
    track: string;
    entities: Array<{
      kg_entity_id: string;
      kg_entity_type: "organization" | "facility" | "project";
    }>;
    reason?: string;
  }): Promise<{
    created_count: number;
    already_existed_count: number;
    total_requested: number;
  }> {
    const client = await getApiClient();
    const response = await client.post(
      "/research/track-entities/bulk_add_entities/",
      data,
    );
    return response.data;
  }

  // Entity Decision operations
  async bulkUpdateDecisions(data: {
    entity_ids: string[];
    decision_state: string;
    decision_meta?: Record<string, any>;
  }): Promise<any> {
    const client = await getApiClient();
    const response = await client.patch(
      "/research/entity-decisions/bulk_update_decision/",
      data,
    );
    return response.data;
  }

  // Requirements Generation operations
  async triggerRequirementsGeneration(
    trackId: string,
    params?: {
      spec_version_id?: string;
      additional_instructions?: string;
    },
  ): Promise<any> {
    const client = await getApiClient();
    const response = await client.post(
      `/research/tracks/${trackId}/trigger_requirements_generation/`,
      params || {},
    );
    return response.data;
  }

  async getRunDetail(runId: string): Promise<any> {
    const client = await getApiClient();
    const response = await client.get(`/research/runs/${runId}/`);
    return response.data;
  }

  // Newest completed AI requirements-generation run for a track that carries a
  // proposal, or null. Runs are ordered -started_at server-side, so the first
  // page is enough. Lets the Requirements page surface a proposal that finished
  // while the user was elsewhere (the live path only applies it when the
  // GenerateRequirementsModal is still mounted and polling).
  async getLatestRequirementsGenerationRun(
    trackId: string,
  ): Promise<any | null> {
    const client = await getApiClient();
    const response = await client.get("/research/runs/", {
      params: { track: trackId, status: "completed" },
    });
    const runs: any[] = response.data?.results ?? response.data ?? [];
    return (
      runs.find(
        (r) =>
          r.run_type === "research" &&
          r.run_summary?.generated_requirements?.criteria,
      ) ?? null
    );
  }

  // List all evaluation runs for a track entity (for version selector)
  async getEvaluationRuns(
    trackEntityId: string,
  ): Promise<EvaluationRunSummary[]> {
    const client = await getApiClient();
    const response = await client.get(
      `/research/track-entities/${trackEntityId}/evaluation-runs/`,
    );
    return response.data;
  }

  // Evaluation detail (slim: criterion results + citation refs, no evidence
  // content — evidence is lazy-loaded per criterion via getCriterionEvidence)
  async getEvaluationDetail(
    trackEntityId: string,
    runId?: string,
  ): Promise<EvaluationDetailResponse> {
    const client = await getApiClient();
    const params = runId ? { run_id: runId } : undefined;
    const response = await client.get(
      `/research/track-entities/${trackEntityId}/evaluation/`,
      { params },
    );
    return response.data;
  }

  // Enriched evidence entries for one criterion of an evaluation, keyed by
  // criterion UUID. Loaded on demand when the evidence section is expanded.
  async getCriterionEvidence(
    trackEntityId: string,
    criterionId: string,
    runId?: string,
  ): Promise<Record<string, EvidenceEntry[]>> {
    const client = await getApiClient();
    const params: Record<string, string> = { criterion_id: criterionId };
    if (runId) params.run_id = runId;
    const response = await client.get(
      `/research/track-entities/${trackEntityId}/evaluation-evidence/`,
      { params },
    );
    return response.data.evidence ?? {};
  }

  // Activity Timeline operations
  async getTrackActivities(
    trackId: string,
    params?: { limit?: number },
  ): Promise<ActivityResponse> {
    const client = await getApiClient();
    const response = await client.get(
      `/research/tracks/${trackId}/activities/`,
      { params },
    );
    return response.data;
  }

  // Export operations
  async exportToExcel(
    trackId: string,
    viewType: "shortlist" | "longlist" | "final" | "all",
    decisionStates?: string[],
    entityIds?: string[],
  ): Promise<Blob> {
    const client = await getApiClient();
    const params: Record<string, string> = {
      track_id: trackId,
      view_type: viewType,
    };

    if (decisionStates && decisionStates.length > 0) {
      params.decision_states = decisionStates.join(",");
    }

    if (entityIds && entityIds.length > 0) {
      params.entity_ids = entityIds.join(",");
    }

    const response = await client.get("/research/track-entities/export/", {
      params,
      responseType: "blob",
    });

    return response.data;
  }
}

export const researchApi = new ResearchApiService();
